import { Story } from '../../types/story';

export const lyonBookshop: Story = {
  id: 'lyon-bookshop',
  title: 'The Bookshop in Lyon',
  excerpt: 'In a quiet bookshop in Lyon\'s old quarter, two women discover that some stories are better lived than read.',
  city: 'Lyon',
  publishDate: '2025-12-07',
  readTime: '24 min read',
  tags: ['♀♀', 'bookshop', 'intellectual', 'sensual'],
  categories: ['queer', 'romantic', 'intense'],
  intensity: 3,
  content: `The bookshop was tucked away on a side street in Vieux Lyon, the kind of place you'd only find if you were looking for it—or if you got lost, which is what happened to me.

I'd been wandering the traboules, those hidden passageways that snake through the old quarter, when I emerged onto a quiet street and saw it: Librairie des Muses. The window display was a carefully curated collection of French philosophy, feminist theory, and queer literature. A handwritten sign in the window read "English spoken. All welcome."

Inside, it smelled like old paper and coffee. The space was small but perfectly organized, with floor-to-ceiling shelves and a small café area in the back. Classical music played softly—something baroque, harpsichord and strings.

She was behind the counter, reading. Dark hair pulled back in a loose bun, reading glasses perched on her nose, completely absorbed in whatever she was reading. She looked up when the bell above the door chimed.

"Bonjour," she said, then switched to English when she saw me hesitate. "Good afternoon. Please, look around. Let me know if you need anything."

Her accent was beautiful—French, but softened by years of speaking English. She couldn't have been much older than me, maybe early thirties, with the kind of understated elegance that French women seem to be born with.

I browsed, pulling books at random, not really looking for anything specific. I was in Lyon for a conference—academic, tedious, the kind where everyone was more interested in networking than actual ideas. I'd skipped the afternoon sessions to explore the city.

"Are you looking for something in particular?" She'd appeared beside me, silent as a cat.

"Just browsing. Though I'm always looking for good recommendations."

"What do you like to read?"

"Theory, mostly. Feminist philosophy. Queer theory. I'm doing my PhD on Judith Butler."

Her eyes lit up. "Ah. A scholar. We don't get many of those who aren't at the university." She pulled a book from a nearby shelf. "Have you read this? Monique Wittig. The Straight Mind. Essential, if you're working on Butler."

"I have, actually. It's brilliant."

"Good." She smiled, and it transformed her entire face. "Then you have excellent taste. I'm Céline, by the way. I own this place."

"Rachel."

We talked for over an hour, standing in the philosophy section, pulling books and debating ideas. She was sharp, well-read, the kind of person who could quote Foucault and Beauvoir from memory. She'd done her doctorate in Paris, she told me, then decided she was tired of academia and opened the bookshop instead.

"I wanted to create a space," she explained, gesturing around us. "Somewhere people could think, could discover ideas that might change them. Academia felt too... constrained. Too concerned with publishing and tenure and politics."

"I'm starting to feel that way too," I admitted. "Sometimes I wonder if I'm just playing a game, you know? Saying the right things to the right people, publishing in the right journals. I'm not sure I'm actually thinking anymore."

"Then stop."

"It's not that simple."

"Isn't it?" She looked at me directly, and I felt something shift in the air between us. "We make it complicated because we're afraid. Afraid of disappointing people, afraid of wasting our education, afraid of being ordinary. But what if ordinary is actually extraordinary? What if running a bookshop is more radical than publishing another paper that three people will read?"

I didn't have an answer for that.

"Stay," she said suddenly. "I'm closing soon. Have coffee with me. Or wine. I have a very good Côtes du Rhône upstairs."

Her apartment was above the shop, accessed by a narrow spiral staircase. It was small but beautiful—exposed stone walls, tall windows overlooking the street, bookshelves everywhere. She poured us wine and we sat on her couch, still talking, the conversation flowing as easily as it had downstairs.

"Can I ask you something?" she said, after we'd finished our first glass.

"Of course."

"Are you queer? I don't mean to be presumptuous, but the way you talk about Butler, about theory... there's something personal in it."

"Yes. I came out in undergrad. It was... complicated. My family is very traditional. Very Catholic. They still think it's a phase."

"I'm sorry."

"It's fine. I mean, it's not fine, but I've made peace with it." I paused. "What about you?"

"Lesbian. Out since I was sixteen. My parents were surprisingly okay with it, actually. Very French about it—as long as I was happy and not making a scene, they didn't care who I loved."

We were sitting closer now, our knees almost touching. The wine had made me bold, or maybe it was just her—the way she looked at me like I was interesting, like I mattered.

"I should probably go," I said, not moving.

"You should," she agreed, also not moving.

"I have a session tomorrow morning. Early."

"Then you definitely should go."

Neither of us moved. The air between us was charged, electric. I could feel my heart beating in my throat.

"Céline," I said quietly.

"Yes?"

"I don't want to go."

She set down her wine glass and turned to face me fully. "Then stay."

I kissed her. Or she kissed me. It didn't matter—what mattered was the way she tasted like wine and possibility, the way her hand came up to cup my face, gentle but sure. The way everything else fell away until it was just us, just this moment.

We moved to her bedroom slowly, taking our time. She undressed me carefully, reverently, like she was unwrapping something precious. I did the same for her, discovering her body inch by inch—the curve of her hip, the softness of her breasts, the way she shivered when I kissed her neck.

"Tell me what you want," she whispered against my skin.

"You. I want you."

She smiled and kissed her way down my body, taking her time, building anticipation until I was trembling. When her mouth finally found where I needed it most, I gasped, my hands fisting in her hair. She was skilled and attentive, reading my body like one of her beloved books, learning what made me gasp, what made me arch into her touch.

When I came, I cried out in French—words I'd learned in class but never used like this, never felt in my bones the way I felt them now.

She kissed her way back up my body and I pulled her close, wanting to give her the same pleasure she'd given me. "Show me," I said. "Show me what you like."

She did. She guided my hands, my mouth, telling me what felt good in a mix of French and English that made everything feel more intimate, more real. I was nervous at first—it had been a while since I'd been with a woman, and never with someone like her, someone so confident and sure of herself. But she was patient and encouraging, and when she finally came, her hand tangled in my hair, I felt a surge of joy that had nothing to do with ego and everything to do with connection.

Afterward, we lay tangled together, the sounds of the city drifting through the open window. Somewhere nearby, someone was playing piano—something classical and melancholy.

"Stay the night," she said quietly. "Skip your morning session. Stay with me."

I should have said no. I should have been responsible, should have gone back to my hotel and prepared for my presentation. But I was tired of should, tired of being responsible.

"Okay," I said. "I'll stay."

We made love again in the early hours of the morning, slower this time, savoring every touch. She showed me things I'd only read about in theory—the way desire could be both intellectual and physical, the way two bodies could communicate without words, the way pleasure could be a form of knowledge.

When I finally left, late the next morning, she walked me to the door of the shop.

"Will you come back?" she asked.

"I leave tomorrow. Back to London."

"I know. But will you come back? To Lyon. To me."

I thought about my life in London—my tiny flat, my research, my careful routine. I thought about the conference I was supposed to be attending, about the paper I was supposed to present. I thought about all the things I was supposed to do, supposed to be.

"Yes," I said. "I'll come back."

And I did. Three weeks later, I was back in Lyon, back in her bookshop, back in her bed. And three months after that, I was packing up my London flat, emailing my supervisor to say I was taking a leave of absence, buying a one-way ticket to Lyon.

It wasn't rational. It wasn't planned. It was the opposite of everything I'd been taught to value—security, stability, a clear career path. But it was also the most honest thing I'd ever done.

I work in the bookshop now, alongside Céline. We host reading groups and author events. We drink wine and debate philosophy and make love in the afternoon when the shop is quiet. I'm still working on my thesis, but slowly, without the pressure of deadlines and expectations. I'm writing because I want to, because I have something to say, not because I need to publish or perish.

Sometimes I think about the person I was before Lyon, before Céline. The person who was so afraid of being ordinary that she forgot to actually live. And I'm grateful—grateful for getting lost in the traboules, grateful for wandering into that bookshop, grateful for having the courage to stay.

Because sometimes the most radical thing you can do is choose happiness over achievement. Choose love over security. Choose a life that feels true over a life that looks impressive.

And sometimes, the best stories aren't the ones you read—they're the ones you live.`
};
